import { Link, useSearchParams } from 'react-router-dom';
import { useAdminAnalyticsQuery, useAnalyticsAttemptsQuery, useAnalyticsListingsQuery } from '@/client/api/analytics/analytics.query';
import type { AnalyticsTrendPoint } from '@/client/types/analytics';
import { fromInputDate } from '@/client/utils/dashboard-trends';

const numberFmt = new Intl.NumberFormat('en-US');
const PAGE_SIZE = 50;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const KINDS = ['login', 'signup', 'acquired', 'churn', 'listings', 'engagements'] as const;
type Kind = (typeof KINDS)[number];

const when = (ts?: number) => {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const usersOn = (date: string, status?: string) => {
  const q = new URLSearchParams({ test: 'all', created: 'custom', from: date, to: date });
  if (status) q.set('status', status);
  return `/admin/users?${q}`;
};

type Cell = { key: string; value: number; to?: string };

/** Daily totals. Login and signup days open the attempt log. Acquired and churn days open the users created that day. A listings day opens those listings. */
const AttemptListPage = () => {
  const [params, setParams] = useSearchParams();
  const rawKind = params.get('kind');
  const kind: Kind = (KINDS as readonly string[]).includes(rawKind || '') ? (rawKind as Kind) : 'login';
  const success = params.get('success') === '0' ? '0' : params.get('success') === '1' ? '1' : '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const day = DAY.test(params.get('day') || '') ? params.get('day')! : '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const isAttempt = kind === 'login' || kind === 'signup';
  const isListingDay = kind === 'listings' && Boolean(day);

  const fromDate = fromInputDate(from);
  const toDate = fromInputDate(to);
  const range = fromDate && toDate ? { from: fromDate, to: toDate } : undefined;

  const overview = useAdminAnalyticsQuery(range, !(day && (isAttempt || kind === 'listings')));
  const attempts = useAnalyticsAttemptsQuery(
    {
      kind: kind === 'signup' ? 'signup' : 'login',
      success,
      start_date: day,
      end_date: day,
      page,
      page_size: PAGE_SIZE,
    },
    Boolean(day && isAttempt),
  );
  const listings = useAnalyticsListingsQuery(
    { start_date: day, end_date: day, page, page_size: PAGE_SIZE },
    isListingDay,
  );

  const title =
    kind === 'signup'
      ? 'Signups'
      : kind === 'acquired'
        ? 'Acquired'
        : kind === 'churn'
          ? 'Churn'
          : kind === 'listings'
            ? 'Listings'
            : kind === 'engagements'
              ? 'Engagements'
              : success === '0'
                ? 'Failed logins'
                : success === '1'
                  ? 'Logins'
                  : 'Login attempts';

  const withParams = (patch: Record<string, string | null>) => {
    const q = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === '') q.delete(key);
      else q.set(key, value);
    }
    q.delete('page');
    return `/admin/analytics/attempts?${q}`;
  };

  const setPage = (next: number) => {
    const q = new URLSearchParams(params);
    if (next <= 1) q.delete('page');
    else q.set('page', String(next));
    setParams(q);
  };

  const charts = overview.data?.charts;
  const countOf = (row: AnalyticsTrendPoint) => row.count ?? row.total ?? 0;
  const countLabel =
    kind === 'acquired' ? 'Acquired' : kind === 'churn' ? 'Churned' : kind === 'listings' ? 'New' : 'Count';
  const seriesFor = (): AnalyticsTrendPoint[] => {
    if (kind === 'signup') return charts?.signup_attempts_trends ?? [];
    if (kind === 'acquired') return charts?.user_acquisition_trends ?? charts?.acquired_trends ?? [];
    if (kind === 'churn') return charts?.user_churn_trends ?? [];
    if (kind === 'listings') return charts?.listings ?? [];
    if (kind === 'engagements') return charts?.engagements ?? [];
    return charts?.login_attempts_trends ?? [];
  };

  const daily: { date: string; cells: Cell[] }[] = seriesFor()
    .slice()
    .reverse()
    .map((row) => {
      if (kind === 'login' || kind === 'signup') {
        return {
          date: row.date,
          cells: [
            { key: 'Successful', value: row.successful ?? 0, to: withParams({ day: row.date, success: '1' }) },
            { key: 'Failed', value: row.failed ?? 0, to: withParams({ day: row.date, success: '0' }) },
            { key: 'Total', value: row.total ?? 0, to: withParams({ day: row.date, success: null }) },
          ],
        };
      }
      const record =
        kind === 'acquired'
          ? usersOn(row.date)
          : kind === 'churn'
            ? usersOn(row.date, 'inactive')
            : kind === 'listings'
              ? withParams({ day: row.date })
              : undefined;
      const label = countLabel;
      return { date: row.date, cells: [{ key: label, value: countOf(row), to: record }] };
    });

  const headers =
    daily[0]?.cells.map((cell) => cell.key) ??
    (isAttempt ? ['Successful', 'Failed', 'Total'] : [countLabel]);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{day ? `${title} · ${day}` : title}</h1>
          <p className="text-xs text-gray-500">
            {day ? (kind === 'listings' ? 'Created that day' : 'Attempt log') : from && to ? `${from} – ${to}` : 'Selected range'}
          </p>
        </div>
        <Link to={day ? withParams({ day: null }) : '/admin'} className="text-sm text-gray-500 hover:text-gray-800">
          {day ? 'All days' : 'Back to overview'}
        </Link>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {day && isAttempt && attempts.isLoading && <p className="px-4 py-8 text-sm text-gray-400">Loading…</p>}

        {day && isAttempt && attempts.isSuccess && (
          <>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Username</th>
                  <th className="px-4 py-2 font-medium">Result</th>
                  <th className="px-4 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attempts.data.rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                      No attempts on this day.
                    </td>
                  </tr>
                )}
                {attempts.data.rows.map((row, i) => {
                  const ok = row.success === true || row.success === 1;
                  return (
                    <tr key={`${row.timestamp}-${row.username}-${i}`}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-700">{when(row.timestamp)}</td>
                      <td className="px-4 py-2 text-gray-900">{row.username || '—'}</td>
                      <td className="px-4 py-2">{ok ? 'Success' : 'Failed'}</td>
                      <td className="max-w-md truncate px-4 py-2 text-gray-500" title={row.error}>
                        {row.error || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
              <span>{numberFmt.format(attempts.data.total)} rows</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
                  disabled={page * (attempts.data.page_size || PAGE_SIZE) >= attempts.data.total}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {day && isAttempt && attempts.isError && (
          <p className="px-4 py-8 text-sm text-gray-500">Could not load the attempt log.</p>
        )}

        {isListingDay && listings.isLoading && <p className="px-4 py-8 text-sm text-gray-400">Loading…</p>}

        {isListingDay && listings.isSuccess && (
          <>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Listing</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listings.data.rows.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-gray-400">No listings on this day.</td>
                  </tr>
                )}
                {listings.data.rows.map((row) => (
                  <tr key={row.eid} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                      {row.date_added ? new Date(row.date_added).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2">
                      {row.eid ? (
                        <Link className="text-gray-900 hover:underline" to={`/admin/listings/${row.eid}`}>
                          {row.name || `Listing #${row.eid}`}
                        </Link>
                      ) : (
                        row.name || '—'
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{row.type || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
              <span>{numberFmt.format(listings.data.total)} rows</span>
              <div className="flex gap-2">
                <button type="button" className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
                  disabled={page * (listings.data.page_size || PAGE_SIZE) >= listings.data.total}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {isListingDay && listings.isError && (
          <p className="px-4 py-8 text-sm text-gray-500">Could not load the listings.</p>
        )}

        {!(day && (isAttempt || kind === 'listings')) && (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-4 py-2 font-medium">Date</th>
                {headers.map((header) => (
                  <th key={header} className="px-4 py-2 font-medium">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {overview.isLoading && (
                <tr>
                  <td colSpan={headers.length + 1} className="px-4 py-8 text-center text-gray-400">Loading…</td>
                </tr>
              )}
              {daily.length === 0 && !overview.isLoading && (
                <tr>
                  <td colSpan={headers.length + 1} className="px-4 py-8 text-center text-gray-400">
                    No rows in this range.
                  </td>
                </tr>
              )}
              {daily.map((row) => (
                <tr key={row.date} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    {row.cells[0]?.to ? (
                      <Link className="text-gray-900 hover:underline" to={isAttempt ? withParams({ day: row.date }) : row.cells[0].to}>
                        {row.date}
                      </Link>
                    ) : (
                      <span className="text-gray-900">{row.date}</span>
                    )}
                  </td>
                  {row.cells.map((cell) => (
                    <td key={cell.key} className="px-4 py-2 tabular-nums">
                      {cell.to ? (
                        <Link className="hover:underline" to={cell.to}>{numberFmt.format(cell.value)}</Link>
                      ) : (
                        numberFmt.format(cell.value)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AttemptListPage;
